/**
 * Step 4: Review & Submit
 * Final review before submitting the transaction.
 * Supports per-variant lots for both single-item and multi-item product modes.
 * Each lot uses its own unit configuration for quantity totals.
 */

import React from 'react';
import {
  Box, Typography, Paper, Grid, Divider, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  KeyboardArrowRight as TransferIcon
} from '@mui/icons-material';

// Calculate total base units from lots using per-lot config
function calcTotal(lots) {
  return (lots || []).reduce((sum, lot) => {
    const upc = parseInt(lot._unitsPerCase) || 1;
    const cpp = parseInt(lot._casesPerPallet) || 1;
    const pQ = parseInt(lot.palletQty) || 0;
    const cQ = parseInt(lot.caseQty) || 0;
    const uQ = parseInt(lot.unitQty) || 0;
    return sum + (pQ * cpp * upc) + (cQ * upc) + uQ;
  }, 0);
}

// Render a single lot entry row
function LotRow({ lot }) {
  const upc = parseInt(lot._unitsPerCase) || 1;
  const cpp = parseInt(lot._casesPerPallet) || 1;
  const pQ = parseInt(lot.palletQty) || 0;
  const cQ = parseInt(lot.caseQty) || 0;
  const uQ = parseInt(lot.unitQty) || 0;
  const lotTotal = (pQ * cpp * upc) + (cQ * upc) + uQ;
  return (
    <Box sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Grid container spacing={1}>
        {lot._configName && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Config</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{lot._configName}</Typography>
          </Grid>
        )}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Quantity</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {[pQ > 0 && `${pQ} pallets`, cQ > 0 && `${cQ} cases`, uQ > 0 && `${uQ} units`].filter(Boolean).join(', ') || '-'}
            {lotTotal > 0 && ` (${lotTotal})`}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Lot / Batch</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {lot.lotNumber || '-'} / {lot.batchNumber || '-'}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Mfg Date</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {lot.mfgDate ? new Date(lot.mfgDate).toLocaleDateString() : '-'}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '-'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function StepReview({
  adjustmentType,
  selectedItems,
  selectedLocation,
  toLocation,
  formData,
  config,
  itemsConfig,
  selectedVariants
}) {
  const isMultiItem = selectedItems.length > 1;
  const isProduct = config.hasUnitConfig;
  const isChemical = config.hasUnitOfMeasure;
  const itemLabel = config.displayName.slice(0, -1);

  const getTypeLabel = () => {
    if (adjustmentType === 'add') return 'Add Stock';
    if (adjustmentType === 'usage') return 'Usage';
    return 'Transfer';
  };

  const getTypeColor = () => {
    if (adjustmentType === 'add') return 'success';
    if (adjustmentType === 'usage') return 'error';
    return 'primary';
  };

  const getTypeIcon = () => {
    if (adjustmentType === 'add') return <AddIcon />;
    if (adjustmentType === 'usage') return <InventoryIcon />;
    return <TransferIcon />;
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Review the details below and click Submit to complete the transaction
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {/* Transaction Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Transaction Type
          </Typography>
          <Chip icon={getTypeIcon()} label={getTypeLabel()} color={getTypeColor()} sx={{ fontWeight: 500 }} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Item Details */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {itemLabel}s ({selectedItems?.length || 0} selected)
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedItems?.map(item => (
                <Chip key={item.id} label={item.name} size="medium" sx={{ fontSize: '0.9rem' }} />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={adjustmentType === 'transfer' ? 6 : 12}>
            <Typography variant="caption" color="text.secondary">
              {adjustmentType === 'transfer' ? 'From Location' : 'Location'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedLocation?.name}
            </Typography>
          </Grid>

          {adjustmentType === 'transfer' && toLocation && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">To Location</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{toLocation.name}</Typography>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Quantity Details */}
        {isMultiItem ? (
          /* ════════════ Multi-Item ════════════ */
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Per-Item Configuration
            </Typography>

            {isProduct ? (
              <Box>
                {selectedItems.map(item => {
                  const cfg = itemsConfig[item.id] || {};
                  const variantIds = cfg.variantIds || (cfg.variantId ? [cfg.variantId] : []);
                  const hasVL = variantIds.length > 0 && cfg.variantLots;

                  const productTotal = hasVL
                    ? Object.values(cfg.variantLots).reduce((sum, lots) => sum + calcTotal(lots), 0)
                    : calcTotal(cfg.lots);

                  return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>{item.name}</Typography>
                      <Grid container spacing={1} sx={{ mb: 1.5 }}>
                        {variantIds.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              Variant{variantIds.length > 1 ? 's' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {(cfg.variantNames || []).map((n, i) => (
                                <Chip key={i} label={n} size="small" sx={{ fontSize: '0.75rem' }} />
                              ))}
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={6} md={6}>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {productTotal > 0 ? `${productTotal.toLocaleString()} base units` : '-'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {hasVL ? (
                        variantIds.map(vid => {
                          const vName = (cfg.variantNames || [])[variantIds.indexOf(vid)] || vid;
                          const vLots = cfg.variantLots[vid] || [];
                          return (
                            <Box key={vid} sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', display: 'block', mb: 0.5 }}>
                                {vName} ({vLots.length} lot{vLots.length !== 1 ? 's' : ''})
                              </Typography>
                              {vLots.map((lot, lotIdx) => (
                                <LotRow key={lotIdx} lot={lot} />
                              ))}
                            </Box>
                          );
                        })
                      ) : (
                        (cfg.lots || []).map((lot, lotIdx) => (
                          <LotRow key={lotIdx} lot={lot} />
                        ))
                      )}
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              /* Non-product multi-item: item cards with per-item lots */
              <Box>
                {selectedItems.map(item => {
                  const cfg = itemsConfig[item.id] || {};
                  const lots = cfg.lots || [];
                  const totalQty = lots.reduce((sum, lot) => sum + (parseFloat(lot.quantity) || 0), 0);
                  const itemUnitType = isChemical ? (item.primary_unit_type || config.unit || 'units') : (config.unit || 'units');
                  const itemUnitQtyPerUnit = isChemical ? parseFloat(item.primary_unit_quantity) : null;
                  const itemUnitMeasure = isChemical ? (item.primary_unit_measure || '') : null;
                  const totalCalc = isChemical && itemUnitQtyPerUnit ? parseFloat((totalQty * itemUnitQtyPerUnit).toFixed(3)) : null;
                  return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lots.length} lot{lots.length !== 1 ? 's' : ''} · {totalQty.toLocaleString()} {itemUnitType} total
                          {totalCalc ? ` (${totalCalc.toLocaleString()} ${itemUnitMeasure})` : ''}
                        </Typography>
                      </Box>
                      {lots.map((lot, lotIdx) => {
                        const lotQty = parseFloat(lot.quantity) || 0;
                        const lotCalc = isChemical && itemUnitQtyPerUnit && lotQty > 0
                          ? parseFloat((lotQty * itemUnitQtyPerUnit).toFixed(3)) : null;
                        return (
                        <Box key={lotIdx} sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                          {lots.length > 1 && (
                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                              Lot #{lotIdx + 1}
                            </Typography>
                          )}
                          <Grid container spacing={1}>
                            <Grid item xs={6} md={3}>
                              <Typography variant="caption" color="text.secondary">Quantity</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {lot.quantity ? parseFloat(lot.quantity).toLocaleString() : '-'} {itemUnitType}
                                {lotCalc ? <><br /><span style={{ color: '#666', fontWeight: 400 }}>({lotCalc.toLocaleString()} {itemUnitMeasure})</span></> : ''}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="caption" color="text.secondary">Lot / Batch</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{lot.lotNumber || '-'} / {lot.batchNumber || '-'}</Typography>
                            </Grid>
                            {adjustmentType === 'add' && (
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {lot.unitCost ? `$${parseFloat(lot.unitCost).toFixed(2)}` : '-'}
                                </Typography>
                              </Grid>
                            )}
                            {lot.expiryDate && (
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">Expiry</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{new Date(lot.expiryDate).toLocaleDateString()}</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        );
                      })}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {adjustmentType === 'add' && !isProduct && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>Vendor & Invoice Details</Typography>
                <Grid container spacing={2}>
                  {formData.vendor && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Vendor</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.vendor.name}</Typography>
                    </Grid>
                  )}
                  {formData.invoiceNumber && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.invoiceNumber}</Typography>
                    </Grid>
                  )}
                  {formData.invoiceDate && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Invoice Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{new Date(formData.invoiceDate).toLocaleDateString()}</Typography>
                    </Grid>
                  )}
                  {formData.poNumber && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">PO Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.poNumber}</Typography>
                    </Grid>
                  )}
                  {formData.poDate && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">PO Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{new Date(formData.poDate).toLocaleDateString()}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {formData.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{formData.notes}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* ════════════ Single Item ════════════ */
          <Box>
            {isProduct ? (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {selectedVariants?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Variant{selectedVariants.length > 1 ? 's' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {selectedVariants.map(v => (
                          <Chip key={v.id} label={v.name} size="small" sx={{ fontSize: '0.85rem' }} />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {(() => {
                        let total;
                        if (selectedVariants?.length > 0 && formData.variantLots) {
                          total = Object.values(formData.variantLots).reduce((sum, lots) => sum + calcTotal(lots), 0);
                        } else {
                          total = calcTotal(formData.lots);
                        }
                        const lotCount = selectedVariants?.length > 0 && formData.variantLots
                          ? Object.values(formData.variantLots).reduce((sum, lots) => sum + lots.length, 0)
                          : (formData.lots || []).length;
                        return total > 0
                          ? `${total.toLocaleString()} base units${lotCount > 1 ? ` (${lotCount} lots)` : ''}`
                          : '-';
                      })()}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Per-variant or flat lots */}
                {selectedVariants?.length > 0 && formData.variantLots ? (
                  selectedVariants.map(variant => {
                    const vLots = formData.variantLots[variant.id] || [];
                    return (
                      <Box key={variant.id} sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', display: 'block', mb: 0.5 }}>
                          {variant.name} ({vLots.length} lot{vLots.length !== 1 ? 's' : ''})
                        </Typography>
                        {vLots.map((lot, lotIdx) => (
                          <LotRow key={lotIdx} lot={lot} />
                        ))}
                      </Box>
                    );
                  })
                ) : (
                  (formData.lots || []).map((lot, lotIdx) => (
                    <LotRow key={lotIdx} lot={lot} />
                  ))
                )}
              </>
            ) : (
              /* Non-product single item: lot cards */
              <Box>
                {(() => {
                  const singleItem = selectedItems[0];
                  const siUnitType = isChemical ? (singleItem?.primary_unit_type || config.unit || 'units') : (config.unit || 'units');
                  const siUnitQtyPerUnit = isChemical ? parseFloat(singleItem?.primary_unit_quantity) : null;
                  const siUnitMeasure = isChemical ? (singleItem?.primary_unit_measure || '') : null;
                  return (formData.nonProductLots || []).map((lot, idx) => {
                    const lots = formData.nonProductLots || [];
                    const lotQty = parseFloat(lot.quantity) || 0;
                    const lotCalc = isChemical && siUnitQtyPerUnit && lotQty > 0
                      ? parseFloat((lotQty * siUnitQtyPerUnit).toFixed(3)) : null;
                    return (
                    <Box key={idx} sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                      {lots.length > 1 && (
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Lot #{idx + 1}
                        </Typography>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Quantity</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {lot.quantity ? parseFloat(lot.quantity).toLocaleString() : '-'} {siUnitType}
                            {lotCalc ? <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>({lotCalc.toLocaleString()} {siUnitMeasure})</Typography> : null}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Lot / Batch</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{lot.lotNumber || '-'} / {lot.batchNumber || '-'}</Typography>
                        </Grid>
                        {lot.unitCost && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>${parseFloat(lot.unitCost).toFixed(2)}</Typography>
                          </Grid>
                        )}
                        {lot.expiryDate && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{new Date(lot.expiryDate).toLocaleDateString()}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                    );
                  });
                })()}
              </Box>
            )}

            {/* Non-product: vendor, invoice, PO */}
            {!isProduct && adjustmentType === 'add' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {formData.vendor && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Vendor</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.vendor.name}</Typography>
                  </Grid>
                )}
                {formData.invoiceNumber && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.invoiceNumber}</Typography>
                  </Grid>
                )}
                {formData.invoiceDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Invoice Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{new Date(formData.invoiceDate).toLocaleDateString()}</Typography>
                  </Grid>
                )}
                {formData.poNumber && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">PO Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.poNumber}</Typography>
                  </Grid>
                )}
                {formData.poDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">PO Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{new Date(formData.poDate).toLocaleDateString()}</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {formData.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{formData.notes}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
